import React, { useEffect, useRef, useState } from "react";
import { listThreads, getThreadMessages, sendMessage, createThread } from "@/lib/messagesApi";
import { useAuth } from "@/contexts/AuthContext";
import { Send, MessageSquare, Loader2, ShieldAlert, AlertTriangle, User } from "lucide-react";

export default function CompanyMessages() {
  const { user } = useAuth() || {};
  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [blockError, setBlockError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listThreads();
        setThreads(data);
        if (data.length > 0) openThread(data[0]);
      } finally {
        setLoadingThreads(false);
      }
    })();
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openThread = async (thread) => {
    setActiveThread(thread);
    setLoadingMsgs(true);
    setBlockError(null);
    setWarnings([]);
    clearInterval(pollRef.current);
    try {
      const { data } = await getThreadMessages(thread.thread_id);
      setMessages(data.messages || []);
    } finally {
      setLoadingMsgs(false);
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await getThreadMessages(thread.thread_id);
        setMessages(data.messages || []);
      } catch (e) {}
    }, 4000);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeThread || sending) return;
    setSending(true);
    setBlockError(null);
    setWarnings([]);
    try {
      const { data } = await sendMessage(activeThread.thread_id, text);
      if (data.blocked) {
        setBlockError(data.block_reasons);
        return;
      }
      if (data.warnings?.length > 0) setWarnings(data.warnings);
      setMessages((prev) => [...prev, data.message]);
      setText("");
      setThreads((prev) =>
        prev.map((t) =>
          t.thread_id === activeThread.thread_id
            ? { ...t, last_message_preview: text.trim().slice(0, 80) }
            : t
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-x py-8 md:py-12">
      <div className="mb-6">
        <span className="overline">Recruiter inbox</span>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Messages</h1>
      </div>

      <div className="flex gap-0 border border-stone-200 rounded-3xl overflow-hidden bg-white" style={{ height: "70vh" }}>

        {/* Thread list */}
        <div className="w-72 shrink-0 border-r border-stone-200 flex flex-col overflow-y-auto">
          {loadingThreads ? (
            <div className="flex items-center justify-center flex-1 text-stone-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 p-6 text-center">
              <MessageSquare size={28} className="text-stone-300 mb-3" />
              <p className="text-sm text-stone-500">No conversations yet.</p>
              <p className="text-xs text-stone-400 mt-1">Start a conversation from a candidate's profile.</p>
            </div>
          ) : (
            threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => openThread(t)}
                className={`w-full text-left px-4 py-4 border-b border-stone-100 hover:bg-stone-50 transition-colors ${
                  activeThread?.thread_id === t.thread_id ? "bg-stone-50 border-l-2 border-l-[#C2410C]" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0 text-xs font-medium">
                    {(t.candidate_name || "?")[0]}
                  </div>
                  <p className="text-sm font-medium truncate">{t.candidate_name}</p>
                  {t.unread_company > 0 && (
                    <span className="ml-auto shrink-0 w-4 h-4 rounded-full bg-[#C2410C] text-white text-[10px] flex items-center justify-center">
                      {t.unread_company}
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-400 truncate pl-9">{t.last_message_preview || "—"}</p>
              </button>
            ))
          )}
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-stone-400">
              <div className="text-center">
                <MessageSquare size={32} className="mx-auto mb-3 text-stone-300" />
                <p className="text-sm">Select a conversation</p>
                <p className="text-xs text-stone-400 mt-1">Or start one from a candidate's profile</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-sm font-medium">
                  {(activeThread.candidate_name || "?")[0]}
                </div>
                <div>
                  <p className="font-medium text-sm">{activeThread.candidate_name}</p>
                  <p className="text-xs text-stone-400">{activeThread.candidate_email}</p>
                </div>
                <div className="ml-auto flex items-center gap-1 text-xs">
                  <ShieldAlert size={12} className="text-emerald-600" />
                  <span className="text-emerald-700 font-medium">Wishire-protected chat</span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {loadingMsgs ? (
                  <div className="flex items-center justify-center h-full text-stone-400">
                    <Loader2 size={18} className="animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-stone-400 text-sm">
                    No messages yet. Start the conversation.
                  </div>
                ) : (
                  messages.map((m) => (
                    <MessageBubble key={m.message_id} msg={m} myId={user?.user_id} />
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              {/* Block error */}
              {blockError && (
                <div className="mx-5 mb-2 rounded-2xl bg-red-50 border border-red-200 p-3">
                  <div className="flex items-start gap-2">
                    <ShieldAlert size={15} className="text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-red-800 mb-1">Message blocked by Wishire</p>
                      {blockError.map((r, i) => <p key={i} className="text-xs text-red-700">• {r}</p>)}
                    </div>
                  </div>
                </div>
              )}

              {/* Warnings */}
              {warnings.length > 0 && (
                <div className="mx-5 mb-2 rounded-2xl bg-amber-50 border border-amber-200 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      {warnings.map((w, i) => <p key={i} className="text-xs text-amber-800">• {w}</p>)}
                    </div>
                  </div>
                </div>
              )}

              {/* Platform notice */}
              <div className="px-5 pb-1">
                <p className="text-[10px] text-stone-400 text-center">
                  🔒 All messages are monitored. Phone numbers, payment details & off-platform contact are blocked automatically.
                </p>
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="px-5 pb-4 pt-2 flex items-end gap-3">
                <textarea
                  value={text}
                  onChange={(e) => { setText(e.target.value); setBlockError(null); setWarnings([]); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                  placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                  rows={2}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-stone-300 focus:outline-none focus:border-stone-500 text-sm resize-none"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="p-3 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-40 transition-colors shrink-0"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, myId }) {
  const isMe = msg.sender_id === myId;
  return (
    <div className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-xs md:max-w-md lg:max-w-lg ${isMe ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isMe
              ? "bg-stone-900 text-white rounded-br-sm"
              : "bg-[#F5F3ED] text-stone-900 rounded-bl-sm"
          }`}
        >
          {msg.text}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-stone-400">
            {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>
          {msg.flagged && (
            <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
              <AlertTriangle size={9} /> flagged
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
